export type DatatableColumnDef<TId extends string = string> = {
  id: TId;
  label: string;
  defaultVisible: boolean;
  /** Fixed table layout width in rem; omit when the table uses fluid column classes. */
  widthRem?: number;
};

export function createDatatableColumnRegistry<TId extends string>(
  columnOrder: readonly TId[],
  defs: readonly DatatableColumnDef<TId>[],
) {
  const defById = new Map(defs.map((column) => [column.id, column]));

  function getColumnDef(id: TId): DatatableColumnDef<TId> {
    return defById.get(id)!;
  }

  function getAvailableColumns(
    isAvailable: (id: TId) => boolean = () => true,
  ): DatatableColumnDef<TId>[] {
    return defs.filter((column) => isAvailable(column.id));
  }

  function getDefaultVisibleColumnIds(
    availableColumns: readonly DatatableColumnDef<TId>[],
  ): Set<TId> {
    const defaults = availableColumns.filter((column) => column.defaultVisible).map((c) => c.id);
    if (defaults.length > 0) {
      return new Set(defaults);
    }
    return new Set(availableColumns.map((column) => column.id));
  }

  function getOrderedVisibleColumns(visibleColumnIds: Set<TId>): TId[] {
    return columnOrder.filter((id) => visibleColumnIds.has(id));
  }

  function sumVisibleColumnWidthRem(visibleColumnIds: Set<TId>): number {
    let total = 0;
    for (const id of visibleColumnIds) {
      total += getColumnDef(id).widthRem ?? 0;
    }
    return total;
  }

  return {
    columnOrder,
    defs,
    getColumnDef,
    getAvailableColumns,
    getDefaultVisibleColumnIds,
    getOrderedVisibleColumns,
    sumVisibleColumnWidthRem,
  };
}

export type DatatableColumnRegistry<TId extends string> = ReturnType<
  typeof createDatatableColumnRegistry<TId>
>;
